import "../../account.scss";

import { useEffect, useRef, useState } from "react";
import InputMask from "react-input-mask";
import { useDispatch, useSelector } from "react-redux";

import TextFieldDisable from "@elements/TextFieldDisable/TextFieldDisable";
import useUser from "@hooks/user/useUser";
import { Button } from "@runwayhealth/runway-components-react";
import { clearSuccess } from "@store/user/userSlice";
import { RootState } from "@types-runway/state";
import { User, UserUpdate } from "@types-runway/user";
import { toastSuccess } from "@utils/utils";
import { validateEmail, validateFullname, validatePhone } from "@utils/forms/utilsFormvalidation";

interface ProfileProps {
  disableProfileForm: boolean;
  user: User;
  error: string;
  setDisableProfileForm: React.Dispatch<React.SetStateAction<boolean>>;
}

const ProfileAccount = ({
  disableProfileForm,
  user,
  error,
  setDisableProfileForm,
}: ProfileProps) => {
  const [userUpdate, setUserUpdate] = useState<UserUpdate>();
  const [errorText, setErrorText] = useState<string>("");
  const dispatch = useDispatch();
  const { success } = useSelector((state: RootState) => state.user);
  const firstUpdate = useRef(true);
  const { updateUser } = useUser();

  useEffect(() => {
    if (firstUpdate.current) {
      firstUpdate.current = false;
      setUserUpdate({
        first_name: user.first_name,
        last_name: user.last_name,
        phone_number: user.phone_number,
        email: user.email,
      });
      return;
    }
    if (error) {
      setErrorText(error);
    }
  }, [userUpdate]);

  useEffect(() => {
    if (success.type === "write") {
      toastSuccess("Success!", "Your profile has been updated");
      dispatch(clearSuccess());
    }
  }, [success]);

  const handlerUserUpdate = (
    event: React.FormEvent<HTMLInputElement> | React.SyntheticEvent,
    name: string
  ) => {
    let value: string;
    if (event.currentTarget) {
      value = (event as React.FormEvent<HTMLInputElement>).currentTarget.value;
    } else {
      value = ((event as React.SyntheticEvent).target as HTMLInputElement).value;
    }

    setUserUpdate((prev) => ({ ...prev, [name]: value }));
  };

  const formIsValid = () => {
    if (!validateFullname(userUpdate?.first_name || "", userUpdate?.last_name || "")) {
      setErrorText("Please enter your full name");
      return false;
    }

    if (!validatePhone(userUpdate?.phone_number || "")) {
      setErrorText("Please use a US phone number");
      return false;
    }

    if (!validateEmail(userUpdate?.email || "")) {
      setErrorText("Please enter your email address");
      return false;
    }

    return true;
  };

  const save = () => {
    if (formIsValid() && userUpdate) {
      updateUser(userUpdate, true);
      setErrorText("");
      setDisableProfileForm(true);
    }
  };

  return (
    <>
      <form className="account-billing-form">
        <div className="account-billing-form-inputs">
          <TextFieldDisable
            type="text"
            disabled={disableProfileForm}
            title="First Name"
            defaultValue={user.first_name}
            onChange={(e: React.FormEvent<HTMLInputElement>) => handlerUserUpdate(e, "first_name")}
          />
          <TextFieldDisable
            type="text"
            disabled={disableProfileForm}
            title="Last Name"
            defaultValue={user.last_name}
            onChange={(e: React.FormEvent<HTMLInputElement>) => handlerUserUpdate(e, "last_name")}
          />
        </div>
        <div className="account-billing-form-inputs">
          <label className="disable-input">
            <span className={"disable-input-title"}>Phone</span>
            <InputMask
              className={
                disableProfileForm ? "disable-input-change" : "disable-input-change custom-input"
              }
              mask="(999) 999-9999"
              maskChar=" "
              size={20}
              disabled={disableProfileForm}
              value={userUpdate?.phone_number}
              onChange={(e: React.SyntheticEvent) => handlerUserUpdate(e, "phone_number")}
            />
          </label>
          <TextFieldDisable
            type="email"
            disabled={disableProfileForm}
            title="Email Address"
            defaultValue={user.email}
            onChange={(e: React.FormEvent<HTMLInputElement>) => handlerUserUpdate(e, "email")}
          />
        </div>
      </form>
      <br />
      {!disableProfileForm && (
        <Button size="md" iconName="ArrowRight" iconPosition="right" isBlock onClick={save}>
          Save
        </Button>
      )}
      {errorText && (
        <div className="error-message-forgot">
          <span className="message-content-forgot">{errorText}</span>
        </div>
      )}
    </>
  );
};

export default ProfileAccount;
